<template>
  <section>
    <BaseSideModal class="portal-form-modal" :width="'640px'" @close="$emit('close')">
      <template #title>
        <div>
          <span>{{ account
            ? $t('carriers.import.form.EditTitle')
            : $t('carriers.import.form.NewTitle') }}</span>
          <p class="portal-form__sub">{{ carrierName }}</p>
        </div>
      </template>

      <div class="portal-form">
        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-position="top"
          @submit.native.prevent
        >
          <el-form-item :label="$t('carriers.import.form.WhichPortal')" prop="portal">
            <el-select
              v-model="form.portal"
              style="width: 100%"
              :class="{ 'portal-form__select--icon': selectedPortal && selectedPortal.icon }"
            >
              <template #prefix>
                <span v-if="selectedPortal && selectedPortal.icon" class="portal-form__prefix">
                  <img class="portal-form__icon" alt="" :src="selectedPortal.icon"/>
                </span>
              </template>
              <el-option
                v-for="p in sortedPortals"
                :key="p.code"
                :value="p.code"
                :label="p.displayName"
              >
                <span class="portal-form__option">
                  <img v-if="p.icon" class="portal-form__icon" alt="" :src="p.icon"/>
                  {{ p.displayName }}
                </span>
              </el-option>
            </el-select>
            <p class="portal-form__hint">{{ $t('carriers.import.form.WhichPortalHint') }}</p>
          </el-form-item>

          <el-form-item :label="$t('carriers.import.form.Label')" prop="label">
            <el-input v-model="form.label" :placeholder="carrierName"/>
          </el-form-item>

          <!-- An API-key portal (Handle) signs in with the key alone: no user. -->
          <el-form-item v-if="!apiKeyPortal" :label="$t('carriers.import.form.PortalUser')" prop="username">
            <el-input v-model="form.username" autocomplete="off"/>
          </el-form-item>

          <el-form-item v-if="!account" :label="passwordLabel" prop="password">
            <!-- No show-password toggle: this field is typed during screen-shares
                 with the client whose portal login it is. Masked, always. -->
            <el-input v-model="form.password" type="password" autocomplete="new-password"/>
            <p v-if="apiKeyPortal" class="portal-form__hint">{{ $t('carriers.import.form.ApiKeyHint') }}</p>
          </el-form-item>

          <!-- A saved login's password is never shown, sent back or edited in place: it is
               replaced through its own window, so the form above can be saved without one. -->
          <el-form-item v-else :label="passwordLabel">
            <div class="portal-form__password">
              <span class="portal-form__password-dots">••••••••</span>
              <span v-if="passwordChangedAt" class="portal-form__password-when">
                · {{ $t('carriers.import.password.ChangedAgo', { when: relativeDay(passwordChangedAt, $i18n.locale) }) }}
              </span>
              <BaseButton is-small type="outline" class="portal-form__password-btn" @click="changingPassword = true">
                {{ $t(apiKeyPortal ? 'carriers.import.password.UpdateApiKey' : 'carriers.import.password.Update') }}
              </BaseButton>
            </div>
            <p v-if="apiKeyPortal" class="portal-form__hint">{{ $t('carriers.import.form.ApiKeyHint') }}</p>
          </el-form-item>

          <!-- Only on a portal that keeps the agency's own book (Handle): its reads then apply
               with nothing held, a paid mark included. Each day's run stays undoable. -->
          <el-form-item v-if="account && trustablePortal">
            <div class="portal-form__trust">
              <el-switch v-model="form.trustPortal"/>
              <span class="portal-form__trust-label">
                {{ $t('carriers.import.trust.Label', { portal: selectedPortal.displayName }) }}
              </span>
            </div>
            <p class="portal-form__hint">{{ $t('carriers.import.trust.Hint', { portal: selectedPortal.displayName }) }}</p>
          </el-form-item>

          <!-- An authenticator-app code (AXA) is only ever typed: no mailbox to forward it. -->
          <p v-if="portalNeedsTwoFa && authenticatorPortal" class="portal-form__hint portal-form__hint--twofa">
            {{ $t('carriers.import.form.AuthenticatorHint', { portal: selectedPortal.displayName }) }}
          </p>

          <template v-else-if="portalNeedsTwoFa">
            <el-form-item :label="$t('carriers.import.form.TwoFaEmail')" prop="twoFaEmail">
              <el-input v-model="form.twoFaEmail" autocomplete="off" placeholder="name@gmail.com"/>
              <p class="portal-form__hint">{{ $t('carriers.import.form.TwoFaEmailHint') }}</p>
            </el-form-item>

            <AllianzForwardingChecklist
              v-if="account"
              :account="account"
              :receiver-address="carrierImportReceiverAddress"
              @updated="$store.dispatch('carrierImportFetchAccounts', true)"
            />
          </template>
        </el-form>

        <!-- Manual 2FA entry, while a run of THIS account sits on the code screen. -->
        <div v-if="account && waitingForCode" class="portal-form__code">
          <p class="portal-form__code-hint">
            {{ authenticatorPortal
              ? $t('carriers.import.twoFa.AuthenticatorBody', { carrier: carrierName })
              : $t('Integrations.allianz.forwarding.ManualCodeNow') }}
          </p>
          <div class="portal-form__code-row">
            <el-input
              v-model="manualCode"
              :maxlength="authenticatorPortal ? 6 : 8"
              placeholder="123456"
              :inputmode="authenticatorPortal ? 'numeric' : null"
              :autocomplete="authenticatorPortal ? 'one-time-code' : 'off'"
              @input="(v) => { manualCode = (v || '').replace(/\D/g, ''); }"
            />
            <BaseButton
              is-small
              :loading="codeSubmitting"
              :disabled="!codeValid"
              @click="submitCode"
            >
              {{ $t('Integrations.allianz.forwarding.ManualCodeSubmit') }}
            </BaseButton>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton v-if="account" type="error" class="portal-form__delete" @click="confirmDelete = true">
          {{ $t('adminTitles.Delete') }}
        </BaseButton>
        <BaseButton type="outline" @click="$emit('close')">{{ $t('adminTitles.cancel') }}</BaseButton>
        <BaseButton :loading="saving" @click="submit">
          {{ account ? $t('employees.SaveChanges') : $t('carriers.import.form.Connect') }}
        </BaseButton>
      </template>

    </BaseSideModal>

    <UpdatePasswordDialog
      v-if="changingPassword && account"
      :account="account"
      :api-key="apiKeyPortal"
      @saved="onPasswordSaved"
      @close="changingPassword = false"
    />

    <BaseDialog
      v-if="confirmDelete"
      :width="'440px'"
      :text-align="'left'"
      :footer-align="'flex-end'"
      :title="$t('carriers.import.form.DeleteTitle')"
      :text="$t('carriers.import.form.DeleteText')"
      :confirm-button-text="$t('adminTitles.Delete')"
      :cancel-button-text="$t('adminTitles.cancel')"
      :confirm-loading="deleting"
      @close="confirmDelete = false"
      @confirm="remove"
    />
  </section>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import AllianzForwardingChecklist
  from '@/views/RenovaDashboard/IntegrationsParts/components/AllianzForwardingChecklist';
import {
  apiError, isJobActive, portalTwoFaKind, relativeDay,
} from '@/utils/carrierImport';
import { carrierIcon } from '@/utils/carrierCatalog';
import UpdatePasswordDialog from './UpdatePasswordDialog';

/**
 * One portal account, in its own window.
 *
 * Before this, every login of the tenant shared a single screen and there was no
 * telling which username and password belonged to which carrier. Here a window
 * is one login: which portal it opens, whose credentials they are, and where its
 * sign-in codes come from. What it brought in is a separate window — settings and
 * history answer different questions and were harder to read stacked in one
 * scroll.
 */
export default {
  name: 'PortalAccountModal',
  components: {
    BaseSideModal, BaseButton, BaseDialog, AllianzForwardingChecklist, UpdatePasswordDialog,
  },
  props: {
    /** The carrier this login belongs to. */
    insuranceCompanyId: { type: String, required: true },
    carrierName: { type: String, default: '' },
    /** The carrier the record is saved as, if any: a new login opens its portal. */
    carrierCode: { type: String, default: null },
    /** An existing account to edit, or null to connect a new one. */
    account: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      saving: false,
      changingPassword: false,
      /** Set when the password was replaced from this window; the account prop is a snapshot. */
      passwordSavedAt: null,
      deleting: false,
      confirmDelete: false,
      manualCode: '',
      codeSubmitting: false,
      form: {
        portal: '',
        label: '',
        username: '',
        password: '',
        twoFaEmail: '',
        trustPortal: false,
      },
      rules: {
        portal: [
          { required: true, message: this.$t('adminTitles.FieldIsRequired'), trigger: 'change' },
        ],
        username: [
          {
            // Not asked for on an API-key portal, so not required there.
            validator: (rule, value, callback) => {
              if (!this.apiKeyPortal && !value) {
                callback(new Error(this.$t('adminTitles.FieldIsRequired')));
                return;
              }
              callback();
            },
            trigger: 'blur',
          },
        ],
        twoFaEmail: [
          // Optional: an account without a mailbox relies on the broker typing
          // the code while a run waits on the code screen.
          {
            pattern: /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,24}$/,
            message: this.$t('telemed.invalidEmail'),
            trigger: 'blur',
          },
          {
            // Anti-loop guard, also enforced server-side: the receiver address —
            // or anything on its domain — is OUR side of the forwarding chain.
            validator: (rule, value, callback) => {
              const v = (value || '').trim().toLowerCase();
              const receiver = (this.carrierImportReceiverAddress || '').toLowerCase();
              const domain = receiver.split('@')[1];
              if (v && (v === receiver || (domain && v.endsWith(`@${domain}`)))) {
                callback(new Error(this.$t('Integrations.allianz.forwarding.ReceiverLoopError')));
                return;
              }
              callback();
            },
            trigger: 'blur',
          },
        ],
        password: [
          {
            // Asked for only when connecting; a saved login changes it through UpdatePasswordDialog.
            validator: (rule, value, callback) => {
              if (!this.account && !value) {
                callback(new Error(this.$t('adminTitles.FieldIsRequired')));
                return;
              }
              callback();
            },
            trigger: 'blur',
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters([
      'carrierImportPortals', 'carrierImportReceiverAddress',
      'carrierImportRuns', 'carrierImportAccountRuns',
    ]),
    /**
     * The portals we can read first, in the backend's order, then the ones we
     * cannot yet, alphabetically. None is disabled: a login for one we cannot
     * read is checked like any other and then not sent (see submit).
     */
    sortedPortals() {
      const portals = this.carrierImportPortals.map((p) => ({ ...p, icon: carrierIcon(p.code) }));
      const real = portals.filter((p) => p.available);
      const later = portals
        .filter((p) => !p.available)
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
      return real.concat(later);
    },
    selectedPortal() {
      return this.sortedPortals.find((p) => p.code === this.form.portal) || null;
    },
    /** The portal's secret is one API key (Handle), not a portal user and password. */
    trustablePortal() {
      return Boolean(this.selectedPortal && this.selectedPortal.trustable);
    },
    apiKeyPortal() {
      return (this.selectedPortal && this.selectedPortal.credentialKind) === 'API_KEY';
    },
    passwordLabel() {
      return this.apiKeyPortal
        ? this.$t('carriers.import.form.ApiKey')
        : this.$t('carriers.import.form.Password');
    },
    /** When the password was last replaced: this window's own change, else the account's. */
    passwordChangedAt() {
      return this.passwordSavedAt || (this.account && this.account.passwordUpdatedAt) || null;
    },
    /** Not for a portal we cannot read: there is no run to send a code to. Nor for an API key. */
    portalNeedsTwoFa() {
      const p = this.selectedPortal;
      return !p || (p.available && portalTwoFaKind(p) !== 'NONE' && !this.apiKeyPortal);
    },
    /** The portal's code comes from an authenticator app (AXA), not an email. */
    authenticatorPortal() {
      return portalTwoFaKind(this.selectedPortal) === 'AUTHENTICATOR';
    },
    codeValid() {
      return this.authenticatorPortal ? /^\d{6}$/.test(this.manualCode) : /^\d{4,8}$/.test(this.manualCode);
    },
    /**
     * A run of this account is in flight and may ask for a sign-in code.
     *
     * Deliberately not gated on waitingTwoFaAt. Requiring it left a window of
     * exactly one 7s poll to notice the field and type a code the portal honours
     * for about five minutes — the same trap the Integrations screen removed,
     * reintroduced here. The primary place to type one is now the card on the
     * Carriers page; this stays as the path for someone already in this window.
     *
     * Read off the store's polled runs rather than a list fetched once when this
     * window opened. That list never refreshed, so a run started from the Read
     * dialog while this was open never appeared, and this box never came up.
     *
     * An authenticator-app code is the exception: it is gated on waitingTwoFaAt,
     * because the worker drops a code older than its request and the app's code
     * changes every 30 seconds — there is nothing useful to type ahead.
     */
    waitingForCode() {
      if (!this.account) return false;
      const id = this.account.scrapeAccountId;
      const fromAccount = this.carrierImportAccountRuns[id] || [];
      return (this.carrierImportRuns.items || [])
        .filter((r) => r.scrapeAccountId === id)
        .concat(fromAccount)
        .some((r) => isJobActive(r) && (!this.authenticatorPortal || Boolean(r.waitingTwoFaAt)));
    },
  },
  created() {
    if (this.account) {
      this.form = {
        portal: this.account.portal,
        label: this.account.label || '',
        username: this.account.username || '',
        password: '',
        twoFaEmail: this.account.twoFaEmail || '',
        trustPortal: Boolean(this.account.trustPortal),
      };
    } else {
      this.form.portal = this.preselectPortal();
    }
  },
  methods: {
    relativeDay,
    onPasswordSaved(item) {
      this.passwordSavedAt = (item && item.passwordUpdatedAt) || new Date().toISOString();
      this.$emit('saved');
    },
    /**
     * A new login most likely opens the portal of the carrier being edited, so
     * offer that one — the carrier the record is saved as, else a readable
     * portal matched on the hints the backend ships with the catalog.
     */
    preselectPortal() {
      const saved = this.carrierImportPortals.find((p) => p.code === this.carrierCode);
      const name = (this.carrierName || '').toLowerCase();
      const match = this.carrierImportPortals.find(
        (p) => p.available && (p.carrierNameHints || []).some((h) => h && name.includes(h.toLowerCase())),
      );
      const fallback = this.carrierImportPortals.find((p) => p.available);
      return (saved || match || fallback || {}).code || '';
    },
    submit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        // A portal we cannot read yet: nothing to send the login to, and nothing
        // is kept of it.
        if (this.selectedPortal && !this.selectedPortal.available) {
          this.$emit('close');
          return;
        }
        this.saving = true;
        const payload = {
          insuranceCompanyId: this.insuranceCompanyId,
          portal: this.form.portal,
          label: this.form.label || this.carrierName,
          // An API-key portal has no user; the stored one is kept (the API needs one).
          username: this.apiKeyPortal
            ? (this.form.username || (this.account && this.account.username) || 'api-key')
            : this.form.username,
          // Blank clears it; null (omitted on create) leaves it unset.
          twoFaEmail: this.form.twoFaEmail || (this.account ? '' : null),
        };
        // Only when connecting: a saved login's password goes through scrape/account/password.
        if (!this.account) payload.password = this.form.password;
        const url = this.account
          ? '/insurance/policy/scrape/account/update'
          : '/insurance/policy/scrape/account/create';
        if (this.account) payload.scrapeAccountId = this.account.scrapeAccountId;
        if (this.account && this.trustablePortal) payload.trustPortal = this.form.trustPortal;

        axios.post(url, payload)
          .then(() => {
            this.$notify.success({ message: this.$t('Integrations.allianz.Saved') });
            this.$emit('saved');
            this.$emit('close');
          })
          .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
          .finally(() => { this.saving = false; });
      });
    },
    remove() {
      this.deleting = true;
      axios.post('/insurance/policy/scrape/account/delete', {
        scrapeAccountId: this.account.scrapeAccountId,
      })
        .then(() => {
          this.$emit('saved');
          this.$emit('close');
        })
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.deleting = false; this.confirmDelete = false; });
    },
    submitCode() {
      if (!this.codeValid || this.codeSubmitting) return;
      this.codeSubmitting = true;
      axios.post('/insurance/policy/scrape/twofa/submit', {
        scrapeAccountId: this.account.scrapeAccountId,
        code: this.manualCode,
      })
        .then(() => {
          this.manualCode = '';
          this.$notify.success({ message: this.$t('Integrations.allianz.forwarding.ManualCodeSent') });
        })
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.codeSubmitting = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
// BaseSideModal sizes its body as the window less a one-line title. The carrier's name under the
// title made the header taller, so the body overran and pushed Cancel / Connect off the bottom of
// the screen. Stacking the three parts lets the body take whatever height the header leaves.
.portal-form-modal ::v-deep .side-modal {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .side-modal__header,
  .side-modal__footer {
    flex: none;
  }

  .side-modal__content {
    flex: 1;
    min-height: 0;
    height: auto;
    padding-bottom: 24px;
  }
}

.portal-form {
  // With Delete there are three buttons: Delete on the left, Cancel and Save together on the right.
  &__delete {
    margin-right: auto;
  }

  &__sub {
    font-size: 13px;
    font-weight: 400;
    color: #8f8f9f;
    margin-top: 4px;
  }

  &__password {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  &__password-dots {
    letter-spacing: 2px;
    color: var(--solid-black-purple);
  }

  &__password-when {
    font-size: 13px;
    color: var(--solid-dark-purple);
  }

  &__password-btn { margin-left: auto; }

  &__trust {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__trust-label {
    font-weight: 500;
  }

  &__hint {
    font-size: 12px;
    line-height: 16px;
    color: #8f8f9f;
    margin-top: 4px;

    // Stands alone where the mailbox field would be, not under a field.
    &--twofa { margin: 0 0 18px; }
  }

  // Scoped styles reach these even though element-ui appends the dropdown to
  // body: the data-v attribute is stamped on the element, not on an ancestor,
  // and travels with it.
  &__option {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__prefix {
    display: flex;
    align-items: center;
    height: 100%;
    padding-left: 6px;
  }

  // The app's input padding beats element-ui's prefix padding, so the name ran
  // under the icon.
  &__select--icon ::v-deep .el-input__inner {
    padding-left: 38px;
  }

  &__icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    object-fit: contain;
  }

  &__code {
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fff6ec;
  }

  &__code-hint {
    font-size: 13px;
    color: #23233c;
    margin-bottom: 8px;
  }

  &__code-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}
</style>
